/**
 * Phone input model for the notifications screen.
 *
 * The user types straight into a single field that shows the dial code:
 *   +1 (305) 555-1234   (US, default)
 *   +52 (551) 234-5678  (MX)
 *
 * The country is derived from what is typed, never stored separately:
 * the field starts as US and switches to MX when the user clears it and
 * types "+52" (a leading "+" is required to change the country, so a US
 * area code such as 520 is never mistaken for Mexico's dial code).
 *
 * Pure module (no imports) so it can be unit-tested in isolation.
 */

export type PhoneCountry = "US" | "MX";

export const PHONE_NATIONAL_DIGITS = 10;

/** Initial value of the field: the US dial code is already there. */
export const DEFAULT_PHONE_VALUE = "+1";

const DIAL_CODES: Record<PhoneCountry, string> = { US: "1", MX: "52" };

export interface ParsedPhoneInput {
  country: PhoneCountry;
  /** Dial code digits typed so far, without "+" (can be partial, e.g. "5"). */
  dialDigits: string;
  /** National number digits (max 10). */
  nationalDigits: string;
}

export const getTemplate = (country: PhoneCountry): string =>
  `+${DIAL_CODES[country]} (000) 000-0000`;

/** Turns whatever is in the field into a normalized model. */
export const parsePhoneInput = (raw: string): ParsedPhoneInput => {
  const digits = raw.replace(/\D/g, "");
  const hasPlus = raw.trimStart().startsWith("+");

  if (hasPlus) {
    if (digits.startsWith("52")) {
      return {
        country: "MX",
        dialDigits: "52",
        nationalDigits: digits.slice(2, 2 + PHONE_NATIONAL_DIGITS),
      };
    }
    if (digits.startsWith("1")) {
      return {
        country: "US",
        dialDigits: "1",
        nationalDigits: digits.slice(1, 1 + PHONE_NATIONAL_DIGITS),
      };
    }
    if (digits === "5") {
      // Typing "+52": keep the "5" and wait for the "2".
      return { country: "MX", dialDigits: "5", nationalDigits: "" };
    }
    if (digits === "") {
      return { country: "US", dialDigits: "", nationalDigits: "" };
    }
    // Unsupported dial code (e.g. "+3"): treat the digits as a US national number.
    return {
      country: "US",
      dialDigits: "1",
      nationalDigits: digits.slice(0, PHONE_NATIONAL_DIGITS),
    };
  }

  // No "+": the user typed or pasted a national number. Drop an explicit
  // leading US country code ("1" + 10 digits).
  const national =
    digits.length > PHONE_NATIONAL_DIGITS && digits.startsWith("1")
      ? digits.slice(1)
      : digits;
  return {
    country: "US",
    dialDigits: "1",
    nationalDigits: national.slice(0, PHONE_NATIONAL_DIGITS),
  };
};

const formatNational = (digits: string): string => {
  if (digits.length === 0) return "";
  const area = digits.slice(0, 3);
  if (digits.length <= 3) return ` (${area}`;
  const exchange = digits.slice(3, 6);
  if (digits.length <= 6) return ` (${area}) ${exchange}`;
  return ` (${area}) ${exchange}-${digits.slice(6)}`;
};

/** Text shown in the field. Never ends with a formatting character. */
export const formatPhoneInput = (parsed: ParsedPhoneInput): string => {
  if (parsed.dialDigits === "" && parsed.nationalDigits === "") return "+";
  return `+${parsed.dialDigits}${formatNational(parsed.nationalDigits)}`;
};

/**
 * Applies a field change and returns the text to show. The displayed text
 * never ends with a formatting character, so backspacing at the end always
 * removes a digit; deleting a formatting character in the middle is a no-op.
 */
export const nextPhoneValue = (raw: string): string => {
  if (raw.replace(/[^\d+]/g, "") === "") return "";
  return formatPhoneInput(parsePhoneInput(raw));
};

export const isValidPhoneInput = (parsed: ParsedPhoneInput): boolean => {
  const { country, nationalDigits } = parsed;
  if (nationalDigits.length !== PHONE_NATIONAL_DIGITS) return false;
  if (country === "US") {
    // NANP: area code and exchange cannot start with 0/1.
    return !/^[01]/.test(nationalDigits) && !/^\d{3}[01]/.test(nationalDigits);
  }
  return true;
};

/** E.164 for a complete, valid input; null otherwise. */
export const toE164FromInput = (parsed: ParsedPhoneInput): string | null =>
  isValidPhoneInput(parsed)
    ? `+${DIAL_CODES[parsed.country]}${parsed.nationalDigits}`
    : null;

/**
 * Splits the template into the part already typed and the remaining
 * placeholder, so the UI can render the rest in a muted color.
 */
export const splitWithPlaceholder = (
  value: string,
  country: PhoneCountry
): { typed: string; rest: string } => {
  const template = getTemplate(country);
  if (value.length >= template.length) return { typed: value, rest: "" };
  return { typed: value, rest: template.slice(value.length) };
};
